/**
 * Dropdown Handler Functions
 * Common handlers for dropdown interactions
 */

/**
 * Create project dropdown toggle handler
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setProjectEditDropdownOpen - Setter for project dropdown open state
 * @param {Function} setters.setFilter - Setter for filter
 * @param {Function} setters.setSelectedClients - Setter for selected clients
 * @param {Function} setters.setSelectedProject - Setter for selected project
 * @param {Function} setters.setTagEditDropdownOpen - Setter for tag dropdown open state
 * @param {Function} setters.setUStartTimeError - Setter for start time error
 * @param {Function} setters.setUEndTimeError - Setter for end time error
 * @param {Function} setters.setUFormatedTimeError - Setter for formatted time error
 * @returns {Function} Toggle handler function
 */
export const createProjectDropdownToggleHandler = ({
  setProjectEditDropdownOpen,
  setFilter,
  setSelectedClients,
  setSelectedProject,
  setTagEditDropdownOpen,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
}) => {
  return () => {
    setProjectEditDropdownOpen((prev) => !prev);
    setFilter("");
    setSelectedClients(new Set());
    setSelectedProject(null);
    setTagEditDropdownOpen(false);
    setUStartTimeError(false);
    setUEndTimeError(false);
    setUFormatedTimeError(false);
  };
};

/**
 * Create project filter change handler
 * @param {Function} setFilter - Setter for filter
 * @param {Function} setSelectedClients - Setter for selected clients
 * @param {Array} clientData - Client data array
 * @returns {Function} Filter change handler function
 */
export const createProjectFilterChangeHandler = (
  setFilter,
  setSelectedClients,
  clientData
) => {
  return (filterValue) => {
    setFilter(filterValue);
    if (filterValue === "") {
      setSelectedClients(new Set());
    } else {
      const matchingClients = clientData.filter((client) =>
        client.projects.some((project) =>
          project.project_name.toLowerCase().includes(filterValue.toLowerCase())
        )
      );
      setSelectedClients(new Set(matchingClients));
    }
  };
};

/**
 * Create client toggle handler
 * @param {Function} setSelectedClients - Setter for selected clients
 * @returns {Function} Client toggle handler function
 */
export const createClientToggleHandler = (setSelectedClients) => {
  return (newSelectedClients) => {
    setSelectedClients(newSelectedClients);
  };
};

/**
 * Create project select handler
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setSprintIdEdit - Setter for sprint ID edit
 * @param {Function} setters.setSprintNameEdit - Setter for sprint name edit
 * @param {Function} setters.setProjectIdEdit - Setter for project ID edit
 * @param {Function} setters.setProjectNameEdit - Setter for project name edit
 * @param {Function} setters.setClientEdit - Setter for client edit
 * @param {Function} setters.setClientIdEdit - Setter for client ID edit
 * @param {Function} setters.setBillableEdit - Setter for billable edit
 * @param {Function} setters.setProjectEditDropdownOpen - Setter for project dropdown open state
 * @returns {Function} Project select handler function
 */
export const createProjectSelectHandler = ({
  setSprintIdEdit,
  setSprintNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setProjectEditDropdownOpen,
}) => {
  return (project, client) => {
    setSprintIdEdit("");
    setSprintNameEdit("");
    setProjectIdEdit(project._id);
    setProjectNameEdit(project.project_name);
    setClientEdit(client.client_name);
    setClientIdEdit(client._id);
    setBillableEdit(project.is_billable);
    setProjectEditDropdownOpen(false);
  };
};

/**
 * Create project toggle handler
 * @param {Function} setSelectedProject - Setter for selected project
 * @param {Object} selectedProject - Currently selected project
 * @returns {Function} Project toggle handler function
 */
export const createProjectToggleHandler = (setSelectedProject, selectedProject) => {
  return (project) => {
    setSelectedProject(selectedProject === project ? null : project);
  };
};

/**
 * Create sprint select handler
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setSprintIdEdit - Setter for sprint ID edit
 * @param {Function} setters.setSprintNameEdit - Setter for sprint name edit
 * @param {Function} setters.setProjectIdEdit - Setter for project ID edit
 * @param {Function} setters.setProjectNameEdit - Setter for project name edit
 * @param {Function} setters.setClientEdit - Setter for client edit
 * @param {Function} setters.setClientIdEdit - Setter for client ID edit
 * @param {Function} setters.setBillableEdit - Setter for billable edit
 * @param {Function} setters.setProjectEditDropdownOpen - Setter for project dropdown open state
 * @returns {Function} Sprint select handler function
 */
export const createSprintSelectHandler = ({
  setSprintIdEdit,
  setSprintNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setProjectEditDropdownOpen,
}) => {
  return (sprint, project, client) => {
    setSprintIdEdit(sprint._id);
    setSprintNameEdit(sprint.sprint_name);
    setProjectIdEdit(project._id);
    setProjectNameEdit(project.project_name);
    setClientEdit(client.client_name);
    setClientIdEdit(client._id);
    setBillableEdit(project.is_billable);
    setProjectEditDropdownOpen(false);
  };
};

/**
 * Create tag input change handler
 * @param {Function} setTagQuery - Setter for tag query
 * @param {Function} setTagIdEdit - Setter for tag ID edit (optional, not used for input changes)
 * @returns {Function} Tag input change handler function
 */
export const createTagInputChangeHandler = (setTagQuery, setTagIdEdit) => {
  return (event) => {
    setTagQuery(event.target.value);
    // Don't set tagIdEdit here - it should only be set when a tag is selected
  };
};

/**
 * Create tag select handler
 * @param {Function} setTagEdit - Setter for tag edit
 * @param {Function} setTagIdEdit - Setter for tag ID edit
 * @param {Function} setTagEditDropdownOpen - Setter for tag dropdown open state
 * @param {Function} setTagQuery - Setter for tag query (optional)
 * @returns {Function} Tag select handler function
 */
export const createTagSelectHandler = (setTagEdit, setTagIdEdit, setTagEditDropdownOpen, setTagQuery) => {
  return (tag) => {
    setTagEdit(tag.tag_name);
    setTagIdEdit(tag._id);
    if (setTagQuery) {
      setTagQuery("");
    }
    setTagEditDropdownOpen(false);
  };
};

/**
 * Create dropdown toggle handler
 * @param {Function} setActiveDropdownId - Setter for active dropdown ID
 * @returns {Function} Dropdown toggle handler function
 */
export const createDropdownToggleHandler = (setActiveDropdownId) => {
  return (itemId) => {
    setActiveDropdownId((prevId) => (prevId === itemId ? null : itemId));
  };
};
